import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import {
  Avatar,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Typography,
} from '@/ui';
import { normalizeMemberBookingSlots } from '@/utils';

import {
  canonicalBookingDigest,
  clearBookingAttempt,
  confirmationCandidateFromMutation,
  confirmationCandidateFromStoredAttemptDetails,
  loadBookingAttempt,
  saveBookingAttempt,
  transitionBookingAttempt,
} from './bookingAttemptState';

import type {
  BookingConfirmationCandidate,
  StoredBookingAttempt,
} from './bookingAttemptState';
import type {
  BrowserSchedulingContext,
  ParticipantAppointmentMutationResult,
  ParticipantAppointmentOperation,
  ParticipantSchedulingContext,
} from '@/features/appointments/types';
import type {
  BookMemberBiweeklyResponse,
  MemberBiweeklyBookingErrorCode,
  MemberChemistryBookingSlot,
} from '@/types';

import { MentoCalBookingDetailRow } from '@/components/MentoCal/BookingDetailRow';
import { MentoCalDatePicker } from '@/components/MentoCal/DatePicker';
import { MemberRescheduleSession } from '@/components/MentoCal/MemberRescheduleSession';
import {
  mentoCalBookingDayHeading,
  MentoCalTimeList,
} from '@/components/MentoCal/TimeList';
import { MentoCalTimeSlotButton } from '@/components/MentoCal/TimeSlotButton';
import { MENTO_LOGO_URL } from '@/components/MentoCal/constants';
import { seriesLengthLabel } from '@/components/MentoCal/seriesLength';
import { FORTY_EIGHT_HOURS_MS } from '@/components/MentoCal/sessionTiming';
import MemberBoundBooking from '@/features/appointments/MemberBoundBooking';
import PlatformAdminBookingPage from '@/features/appointments/PlatformAdminBookingPage';
import { SeriesCoachConflictNote } from '@/features/appointments/SeriesCoachConflictNote';
import { appointmentActionSession } from '@/features/appointments/appointmentActionSession';
import {
  APPOINTMENT_CONFIRMATION_PAUSED_MESSAGE,
  appointmentPendingMessage,
  appointmentRejectionMessage,
  PENDING_APPOINTMENT_PANEL_TEST_ID,
} from '@/features/appointments/appointmentStatusCopy';
import {
  EXACT_APPOINTMENT_CONTRACT,
  PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
} from '@/features/appointments/browserCapability';
import { retainedFutureSeriesRescheduleCommand } from '@/features/appointments/futureSeriesRescheduleCommand';
import { useOnBehalfBookingTarget } from '@/features/appointments/onBehalfBookingTarget';
import { shouldReconcileParticipantAppointmentWrite } from '@/features/appointments/participantAppointmentError';
import { subscribeReceiptOwner } from '@/features/appointments/receiptOwnerChannel';
import { toLegacyMemberBookingContext } from '@/features/appointments/schedulingContextAdapter';
import { appointmentUpgradeGuardSpent } from '@/features/appointments/useAppointmentLifecycleGuard';
import {
  appointmentApi,
  isTrustedCalendarEventTitleStale,
  shouldReconcileAppointmentWrite,
  trustedAppointmentDeclineMessage,
  useBookAppointmentMutation,
  useGetAppointmentOperationQuery,
  useGetAppointmentQuery,
  useGetAppointmentSchedulingContextQuery,
  useGetAppointmentsQuery,
  useLazyGetAppointmentQuery,
  useLazyGetAppointmentsQuery,
} from '@/store/api/appointmentApi';
import {
  useBookMomentsParticipantAppointmentMutation,
  useGetMomentsParticipantAppointmentOperationQuery,
  useGetMomentsParticipantAppointmentQuery,
  useGetMomentsParticipantSchedulingContextQuery,
  useLazyGetMomentsParticipantAppointmentQuery,
  useParticipantAppointmentRouteDecision,
} from '@/store/api/momentsParticipantAppointmentApi';
import { useAppDispatch } from '@/store/hooks';

// Bi-weekly page defaults to a 14-day window (vs chem-call's 7-day default)
// to satisfy the design's "next 10 days" copy with one whole row of headroom.
const DEFAULT_VISIBLE_WEEKS = 2;
const MAX_VISIBLE_WEEKS = 3;
const CONFIRMATION_DETAIL_RETRY_MS = 5000;
const MAX_CONFIRMATION_DETAIL_RETRIES = 12;
const CONFIRMATION_DETAIL_REVIEW_MESSAGE =
  'We could not validate the confirmed booking details. This booking requires manual review before you try another time.';

const DIVIDER = '#E3E3E3';
const ADMIN_COACH_CONFLICT_MESSAGE =
  'You cannot book on behalf of this member while signed in as the selected coach. Select a different coach to continue.';

const getViewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const participantRejection = (
  result:
    ParticipantAppointmentMutationResult | ParticipantAppointmentOperation,
) => {
  if (result.error) {
    return { code: result.error.error, message: result.error.error };
  }
  if (result.status === 'needs_reconciliation') {
    return {
      code: 'operation_needs_reconciliation',
      message: 'The appointment operation requires manual review.',
    };
  }
  return null;
};

const isRestorablePendingAttempt = (
  attempt: StoredBookingAttempt | null,
): attempt is StoredBookingAttempt & { pendingOperationId: string } =>
  Boolean(
    attempt?.status === 'pending' &&
    attempt.pendingOperationId &&
    (attempt.confirmationCandidate ||
      (attempt.transport === 'moments' && attempt.confirmationExpectation)),
  );

interface ErrorWithData {
  data?: {
    code?: string;
    error?: string;
    write_outcome?: 'not_applied' | 'not_applicable' | 'unknown';
  };
  status?: number | string;
}

// Extracts the structured `code` from an RTK Query error payload so the page
// can render the correct block screen for engagement_end_missing /
// engagement_ended without parsing the human-readable message.
const errorCode = (
  error: unknown,
): MemberBiweeklyBookingErrorCode | undefined => {
  if (error && typeof error === 'object' && 'data' in error) {
    const { data } = error as ErrorWithData;
    if (data && typeof data === 'object' && typeof data.code === 'string') {
      return data.code as MemberBiweeklyBookingErrorCode;
    }
  }
  return undefined;
};

const bookingErrorMessage = (error: unknown) => {
  // A trusted decline forwards an internal machine token in `error`
  // (recurring_coaching_create_disabled is live today on this path). The
  // passthrough below would render it verbatim, so declines are converted to
  // canned copy here, at the one funnel every booking failure on this page goes
  // through. Unknown reasons still resolve to safe copy.
  const declined = trustedAppointmentDeclineMessage('booking', error);
  if (declined) return declined;

  if (error && typeof error === 'object' && 'data' in error) {
    const { data } = error as ErrorWithData;
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  }
  return 'That time is no longer available. Please choose another time.';
};

const ownedBookingErrorMessage = (operationId: string) =>
  `We could not safely confirm this booking. Operation ${operationId} may still commit; contact support before booking another time.`;

const sameAppointmentIds = (left: string[], right: string[]) =>
  left.length === right.length &&
  new Set(left).size === left.length &&
  left.every((id) => right.includes(id));

interface BookingAttempt {
  cadenceWeeks: number;
  coachPersonId: string;
  firstStart: string;
  generation: number;
  idempotencyKey: string;
  memberPersonId: string;
  occurrenceCount: number;
  routeKey: string;
}

type BookingAttemptPhase =
  'confirmed' | 'failed' | 'idle' | 'pending' | 'reconciling' | 'submitting';

const matchingActiveSeries = (
  context: BrowserSchedulingContext | ParticipantSchedulingContext,
  attempt: BookingAttempt,
) => {
  const { activeSeries } = context;
  return activeSeries &&
    context.coach.personId === attempt.coachPersonId &&
    context.member.personId === attempt.memberPersonId &&
    activeSeries.cadenceWeeks === attempt.cadenceWeeks &&
    activeSeries.remainingAppointmentCount === attempt.occurrenceCount &&
    Date.parse(activeSeries.nextSchedule.startAt) ===
      Date.parse(attempt.firstStart)
    ? activeSeries
    : null;
};

const slotStartTime = (slot: MemberChemistryBookingSlot) => slot.startTime;

const seatTitle = (seatType?: string) =>
  seatType === 'executive'
    ? 'Bi-Weekly Executive Coaching'
    : 'Bi-Weekly Leadership Coaching';

const seatTypeLabel = (seatType?: string) =>
  `Seat type: ${seatType === 'executive' ? 'Executive' : 'Leadership'}`;

/* ---------- Block screens for engagement-end gates ---------- */

const BlockScreen = ({
  title,
  body,
  onDone,
}: {
  body: string;
  title: string;
  onDone: () => void;
}) => (
  <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4 py-8">
    <Paper
      className="max-w-md p-8 text-center"
      elevation={8}
      style={{ borderRadius: 20 }}
    >
      <Typography className="mb-2" variant="h5">
        {title}
      </Typography>
      <Typography className="text-cement-400">{body}</Typography>
      <div className="mt-4 flex justify-center">
        <Button onClick={onDone}>Back to home</Button>
      </div>
    </Paper>
  </main>
);

/* ---------- Confirmation screen ---------- */

const BookingConfirmation = ({
  calendarEventTitle,
  series,
  coachFirstName,
  coachLastName,
  coachName,
  coachAvatarUrl,
  durationMinutes,
  memberEmail,
  providerPending = false,
  seatTypeTitle,
  timezone,
  onDone,
}: {
  calendarEventTitle?: string;
  coachAvatarUrl?: string;
  coachFirstName: string;
  coachLastName: string;
  coachName?: string;
  durationMinutes?: number;
  memberEmail?: string;
  onDone: () => void;
  // MEN-4915: true while the Google artifact is still settling. The booking is
  // already durable, but the calendar invite does not exist yet and the
  // operation can still end in needs_reconciliation, so the copy must not
  // assert an invitation that may never be sent.
  providerPending?: boolean;
  seatTypeTitle: string;
  series: BookMemberBiweeklyResponse['series'];
  timezone: string;
}) => {
  // Bind to series.firstStart from the server response — NOT the React click
  // state — so DST or coach-timezone normalization downstream stay correct.
  const start = new Date(series.firstStart);
  const dayOfWeek = start.toLocaleDateString('en', {
    timeZone: timezone,
    weekday: 'long',
  });
  const timeLabel = start.toLocaleTimeString('en', {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone: timezone,
  });
  const dateLabel = start.toLocaleDateString('en', {
    day: 'numeric',
    month: 'short',
    timeZone: timezone,
    year: 'numeric',
  });

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-sunny-100 px-4 py-8">
      <IconButton
        aria-label="Close"
        className="absolute right-4 top-4"
        onClick={onDone}
      >
        <Icon>close</Icon>
      </IconButton>
      <div className="flex w-full flex-col items-center gap-16">
        <img alt="Mento" className="h-8" src={MENTO_LOGO_URL} />
        <Paper
          aria-label={`Coaching with ${coachFirstName} is set`}
          className="w-full max-w-[481px] p-8 text-left"
          elevation={3}
          role="region"
          style={{ border: `1px solid ${DIVIDER}`, borderRadius: 16 }}
        >
          <div className="mb-4 flex items-center gap-4">
            <Avatar
              firstName={coachFirstName}
              lastName={coachLastName}
              size="s"
              src={coachAvatarUrl ?? ''}
            />
            <Typography className="text-base font-bold">{coachName}</Typography>
          </div>
          <Typography className="mb-4 text-2xl font-bold" variant="h5">
            Coaching with {coachFirstName} is set!
          </Typography>
          <Typography className="mb-2.5 text-sm leading-normal text-cement-400">
            {providerPending ? (
              <>
                Booked for <strong>{memberEmail}</strong>
              </>
            ) : (
              <>
                <strong>{memberEmail}</strong> has been invited to
              </>
            )}
          </Typography>
          <div className="mb-2.5">
            <Typography className="text-base leading-tight text-cement-400">
              {seatTypeTitle}
            </Typography>
            <Typography className="text-base leading-tight text-cement-400">
              {seatTypeLabel(
                seatTypeTitle.includes('Executive')
                  ? 'executive'
                  : 'leadership',
              )}
            </Typography>
            {durationMinutes ? (
              <Typography className="text-base leading-tight text-cement-400">
                {durationMinutes} Minutes
              </Typography>
            ) : null}
            {calendarEventTitle ? (
              <Typography className="text-base leading-tight text-cement-400">
                {calendarEventTitle}
              </Typography>
            ) : null}
            <Typography className="text-base font-bold leading-tight">
              Every other {dayOfWeek} at {timeLabel}
            </Typography>
            <Typography className="text-base leading-tight text-cement-400">
              {series.occurrenceCount} appointments in {timezone}
            </Typography>
            <Typography className="text-base leading-tight text-cement-400">
              beginning on {dateLabel} with {coachName ?? coachFirstName}
            </Typography>
          </div>
          {providerPending ? (
            <Typography className="mb-1 text-sm leading-normal text-cement-400">
              Your sessions are booked. Calendar setup is still processing.
            </Typography>
          ) : (
            <Typography className="mb-1 text-sm leading-normal text-cement-400">
              This session will appear as &quot;Private&quot; on your calendar.
            </Typography>
          )}
          <Typography className="text-sm leading-normal text-cement-400">
            Sessions must be rescheduled more than 48 hours before the scheduled
            time.
            {providerPending
              ? ''
              : ' You can reschedule using the link in your calendar invitation.'}
          </Typography>
          <div className="mt-4 flex justify-end">
            <Button variant="contained" onClick={onDone}>
              Done
            </Button>
          </div>
        </Paper>
      </div>
    </main>
  );
};

/* ---------- Main booking page ---------- */

// eslint-disable-next-line complexity -- Booking flow keeps API state, slot selection, and confirmation transitions together.
const ParticipantBiweeklyBooking = () => {
  const { coachId = '' } = useParams();
  const memberId = undefined;
  const { pathname, state } = useLocation();
  const requiresActiveSeries = state?.intent === 'reschedule-series';
  const isAdminMode = Boolean(memberId);
  const returnPath = isAdminMode
    ? `/coach/manage/coaches/${coachId}/schedule`
    : '/m';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isUserLoading } = useCurrentUser();
  const effectiveMemberId = isUserLoading ? '' : user.id;
  const routeKey =
    effectiveMemberId && (!isAdminMode || (!isUserLoading && user.id))
      ? `${isAdminMode ? `${user.id}:` : ''}${coachId}:${effectiveMemberId}`
      : '';
  const hasAdminCoachConflict = isAdminMode && user.id === coachId;
  const [visibleWeeks, setVisibleWeeks] = useState(DEFAULT_VISIBLE_WEEKS);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] =
    useState<MemberChemistryBookingSlot | null>(null);
  const [isTimeMenuOpen, setIsTimeMenuOpen] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [requiresFreshSchedulingContext, setRequiresFreshSchedulingContext] =
    useState(false);
  const [attemptPhase, setAttemptPhase] = useState<BookingAttemptPhase>('idle');
  const attemptGeneration = useRef(0);
  const routeInstanceToken = useRef<object>({});
  const retainedContextRouteKey = useRef<string | null>(null);
  const rejectionRecoveryOperation = useRef<string | null>(null);
  const confirmationRecoveryOperation = useRef<string | null>(null);
  const confirmationDetailRetryTimer = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const restoredConfirmationReceipt = useRef<string | null>(null);
  const currentAttempt = useRef<BookingAttempt | null>(null);
  const submissionClaimed = useRef(false);
  const releaseSubmissionClaim = () => {
    submissionClaimed.current = false;
  };
  const isCurrentRouteInstance = (token: object) =>
    routeInstanceToken.current === token;
  const isCurrentAttempt = (
    attempt: BookingAttempt,
    generation: number,
    token: object,
  ) =>
    isCurrentRouteInstance(token) &&
    attemptGeneration.current === generation &&
    currentAttempt.current?.routeKey === attempt.routeKey;
  const [pendingOperation, setPendingOperation] = useState<{
    operationId: string;
    retryAfterSeconds?: number | null;
    generation: number;
    occurrenceCount: number;
    routeKey: string;
    startAt: string;
    timezone: string;
  } | null>(null);
  const [operationPollingStopped, setOperationPollingStopped] = useState(false);
  const [confirmationDetailAttempt, setConfirmationDetailAttempt] = useState(0);
  // Set when an admitted HTTP 202 pending attempt cannot be durably persisted.
  // The route then keeps the operation in memory and displays its reference
  // while blocking resubmission and navigation that would drop it.
  const [pendingStorageUnavailable, setPendingStorageUnavailable] =
    useState(false);
  const [confirmedSeries, setConfirmedSeries] = useState<
    BookMemberBiweeklyResponse['series'] | null
  >(null);
  const [storedAttempt, setStoredAttempt] =
    useState<StoredBookingAttempt | null>(() =>
      routeKey ? loadBookingAttempt(routeKey) : null,
    );
  const confirmedAttemptRef = useRef<StoredBookingAttempt | null>(null);
  confirmedAttemptRef.current =
    storedAttempt?.status === 'confirmed' ? storedAttempt : null;
  const [confirmationCandidate, setConfirmationCandidate] =
    useState<BookingConfirmationCandidate | null>(null);
  const [confirmationTimezone, setConfirmationTimezone] = useState<
    string | null
  >(null);
  const viewerTimezone = useMemo(getViewerTimezone, []);
  const routeDecision = useParticipantAppointmentRouteDecision({
    coachExternalId: coachId || undefined,
    establishedTransport:
      storedAttempt?.routeKey === routeKey
        ? (storedAttempt.transport ?? 'rails')
        : undefined,
    flow: 'booking',
  });
  const selectedTransport =
    routeDecision.state === 'ready' ? routeDecision.transport : undefined;

  useEffect(() => {
    const token = {};
    routeInstanceToken.current = token;
    const cleanup = () => {
      if (routeInstanceToken.current === token) {
        routeInstanceToken.current = {};
      }
      currentAttempt.current = null;
      rejectionRecoveryOperation.current = null;
      confirmationRecoveryOperation.current = null;
      if (confirmationDetailRetryTimer.current) {
        clearTimeout(confirmationDetailRetryTimer.current);
        confirmationDetailRetryTimer.current = null;
      }
      restoredConfirmationReceipt.current = null;
      submissionClaimed.current = false;
    };
    setVisibleWeeks(DEFAULT_VISIBLE_WEEKS);
    setSelectedDate('');
    setSelectedSlot(null);
    setIsTimeMenuOpen(false);
    setBookingError('');
    setRequiresFreshSchedulingContext(false);
    setOperationPollingStopped(false);
    setPendingStorageUnavailable(false);
    currentAttempt.current = null;
    rejectionRecoveryOperation.current = null;
    confirmationRecoveryOperation.current = null;
    setConfirmationDetailAttempt(0);
    restoredConfirmationReceipt.current = null;
    submissionClaimed.current = false;
    if (!routeKey) return cleanup;
    const restored = loadBookingAttempt(routeKey);
    if (!isCurrentRouteInstance(token)) return cleanup;
    if (requiresActiveSeries && restored?.status === 'confirmed') {
      clearBookingAttempt(routeKey);
      setStoredAttempt(null);
      setConfirmationCandidate(null);
      attemptGeneration.current += 1;
      setAttemptPhase('idle');
      setPendingOperation(null);
      setConfirmedSeries(null);
      setConfirmationTimezone(null);
      return cleanup;
    }
    setStoredAttempt(restored);
    setConfirmationCandidate(restored?.confirmationCandidate ?? null);
    if (restored?.status === 'confirmed' && restored.confirmationCandidate) {
      const candidate = restored.confirmationCandidate;
      restoredConfirmationReceipt.current = restored.idempotencyKey;
      attemptGeneration.current = restored.generation;
      setAttemptPhase('confirmed');
      setConfirmedSeries({
        firstStart: candidate.firstStart,
        intervalWeeks: candidate.cadenceWeeks,
        occurrenceCount: candidate.occurrenceCount,
        seriesId: candidate.seriesId,
        status: 'confirmed',
      });
      setConfirmationTimezone(candidate.timezone);
      setPendingOperation(null);
    } else if (isRestorablePendingAttempt(restored)) {
      const candidate = restored.confirmationCandidate;
      const expectation = restored.confirmationExpectation;
      attemptGeneration.current = restored.generation;
      submissionClaimed.current = true;
      setAttemptPhase('pending');
      setPendingOperation({
        generation: restored.generation,
        occurrenceCount:
          candidate?.occurrenceCount ?? expectation!.occurrenceCount,
        operationId: restored.pendingOperationId,
        retryAfterSeconds: restored.retryAfterSeconds,
        routeKey: restored.routeKey,
        startAt: candidate?.firstStart ?? expectation!.firstStart,
        timezone: candidate?.timezone ?? viewerTimezone,
      });
      if (candidate) {
        // MEN-4915: a member returning while Google is still settling must keep
        // seeing their booking, not fall back to a spinner.
        setConfirmedSeries({
          firstStart: candidate.firstStart,
          intervalWeeks: candidate.cadenceWeeks,
          occurrenceCount: candidate.occurrenceCount,
          seriesId: candidate.seriesId,
          status: 'confirmed',
        });
        setConfirmationTimezone(candidate.timezone);
      } else {
        setConfirmedSeries(null);
        setConfirmationTimezone(null);
      }
    } else if (restored) {
      attemptGeneration.current = restored.generation;
      setAttemptPhase(
        restored.status === 'error' || restored.status === 'rejected'
          ? 'failed'
          : restored.status,
      );
      setPendingOperation(null);
      setConfirmedSeries(null);
      setConfirmationTimezone(null);
      setBookingError(
        restored.status === 'rejected'
          ? (restored.errorMessage ??
              'That time is no longer available. Please choose another time.')
          : '',
      );
    } else {
      attemptGeneration.current += 1;
      setAttemptPhase('idle');
      setPendingOperation(null);
      setConfirmedSeries(null);
      setConfirmationTimezone(null);
    }
    return cleanup;
  }, [requiresActiveSeries, routeKey]);

  useEffect(() => {
    if (!routeKey) return undefined;
    const token = routeInstanceToken.current;
    return subscribeReceiptOwner(routeKey, () => {
      if (
        !isCurrentRouteInstance(token) ||
        currentAttempt.current ||
        confirmationRecoveryOperation.current
      )
        return;
      const receipt = loadBookingAttempt(routeKey);
      if (!receipt) return;
      if (isRestorablePendingAttempt(receipt)) {
        const candidate = receipt.confirmationCandidate;
        const expectation = receipt.confirmationExpectation;
        attemptGeneration.current = receipt.generation;
        submissionClaimed.current = true;
        setStoredAttempt(receipt);
        setConfirmationCandidate(candidate);
        setAttemptPhase('pending');
        setPendingOperation({
          generation: receipt.generation,
          occurrenceCount:
            candidate?.occurrenceCount ?? expectation!.occurrenceCount,
          operationId: receipt.pendingOperationId,
          retryAfterSeconds: receipt.retryAfterSeconds,
          routeKey: receipt.routeKey,
          startAt: candidate?.firstStart ?? expectation!.firstStart,
          timezone: candidate?.timezone ?? viewerTimezone,
        });
        if (candidate) {
          // MEN-4915: see the restore path above -- a pending operation no
          // longer hides the booking.
          setConfirmedSeries({
            firstStart: candidate.firstStart,
            intervalWeeks: candidate.cadenceWeeks,
            occurrenceCount: candidate.occurrenceCount,
            seriesId: candidate.seriesId,
            status: 'confirmed',
          });
          setConfirmationTimezone(candidate.timezone);
        } else {
          setConfirmedSeries(null);
          setConfirmationTimezone(null);
        }
        return;
      }
      if (receipt.status === 'error' || receipt.status === 'rejected') {
        attemptGeneration.current = receipt.generation;
        setStoredAttempt(receipt);
        setAttemptPhase('failed');
        setPendingOperation(null);
        setConfirmedSeries(null);
        setConfirmationTimezone(null);
        setBookingError(receipt.errorMessage ?? '');
        return;
      }
      if (receipt.status !== 'confirmed' || !receipt.confirmationCandidate)
        return;
      if (requiresActiveSeries) {
        clearBookingAttempt(routeKey);
        return;
      }
      const candidate = receipt.confirmationCandidate;
      restoredConfirmationReceipt.current = receipt.idempotencyKey;
      attemptGeneration.current = receipt.generation;
      setStoredAttempt(receipt);
      setConfirmationCandidate(candidate);
      setAttemptPhase('confirmed');
      setConfirmedSeries({
        firstStart: candidate.firstStart,
        intervalWeeks: candidate.cadenceWeeks,
        occurrenceCount: candidate.occurrenceCount,
        seriesId: candidate.seriesId,
        status: 'confirmed',
      });
      setConfirmationTimezone(candidate.timezone);
      setPendingOperation(null);
    });
  }, [requiresActiveSeries, routeKey]);

  useEffect(() => {
    const bookingPathname = pathname;
    const ownedRouteKey = routeKey;
    return () => {
      const confirmedAttempt = confirmedAttemptRef.current;
      if (
        ownedRouteKey &&
        window.location.pathname !== bookingPathname &&
        confirmedAttempt?.routeKey === ownedRouteKey
      ) {
        clearBookingAttempt(ownedRouteKey);
      }
    };
  }, [pathname, routeKey]);

  const skipBookingDetailsQuery =
    isUserLoading || !coachId || selectedTransport !== 'rails';

  const {
    currentData,
    data: retainedData,
    error: getError,
    isFetching,
    isLoading,
    refetch: refetchSchedulingContext,
  } = useGetAppointmentSchedulingContextQuery(
    { coachId, kind: 'coaching', memberId, viewerTimezone, visibleWeeks },
    {
      skip: skipBookingDetailsQuery,
    },
  );
  const momentsContextQuery = useGetMomentsParticipantSchedulingContextQuery(
    coachId && selectedTransport === 'moments'
      ? {
          counterpart: { externalId: coachId, provider: 'legacy_app' },
          kind: 'coaching',
          viewerTimezone,
          visibleWeeks,
        }
      : skipToken,
  );
  const selectedCurrentData =
    selectedTransport === 'moments'
      ? momentsContextQuery.currentData
      : currentData;
  const selectedRetainedData =
    selectedTransport === 'moments' ? momentsContextQuery.data : retainedData;
  const refetchAuthoritativeSchedulingContext =
    selectedTransport === 'moments'
      ? momentsContextQuery.refetch
      : refetchSchedulingContext;
  const currentContext = selectedCurrentData
    ? toLegacyMemberBookingContext(selectedCurrentData)
    : undefined;
  useEffect(() => {
    if (selectedCurrentData) retainedContextRouteKey.current = routeKey;
  }, [routeKey, selectedCurrentData]);
  const routeOwnsRetainedContext = retainedContextRouteKey.current === routeKey;
  const authoritativeSchedulingContext =
    selectedCurrentData ??
    (routeOwnsRetainedContext ? selectedRetainedData : undefined);
  const retainedContext =
    !currentContext && authoritativeSchedulingContext
      ? toLegacyMemberBookingContext(authoritativeSchedulingContext)
      : undefined;
  // Fall back to the last successful result (`retainedData`) while a Show More
  // refetch swaps args — `currentData` is undefined for the new arg mid-flight,
  // which would otherwise blank the screen via the `(isFetching && !data)` gate.
  // Never retain a different coach or member across route transitions.
  const data = currentContext ?? retainedContext;
  const authoritativeActiveSeries = !isAdminMode
    ? authoritativeSchedulingContext?.activeSeries
    : null;
  const authoritativeCoachPersonId =
    authoritativeSchedulingContext?.coach.personId ?? data?.coach.id;
  const authoritativeMemberPersonId =
    authoritativeSchedulingContext?.member.personId ?? data?.member.id;
  const retainedFutureSeriesSelection = !isAdminMode
    ? retainedFutureSeriesRescheduleCommand({
        coachPersonId: authoritativeCoachPersonId,
        memberPersonId: authoritativeMemberPersonId,
      })
    : { kind: 'none' as const };
  const retainedFutureSeriesCommand =
    retainedFutureSeriesSelection.kind === 'selected'
      ? retainedFutureSeriesSelection.command
      : null;
  const activeSeriesAppointments = useGetAppointmentsQuery(
    authoritativeActiveSeries && retainedFutureSeriesSelection.kind === 'none'
      ? {
          seriesId: authoritativeActiveSeries.id,
          userId: user.id,
        }
      : skipToken,
  );
  const activeSeriesAppointmentPage =
    activeSeriesAppointments.currentData ?? activeSeriesAppointments.data;
  const seriesOccurrences =
    activeSeriesAppointmentPage?.appointments.filter(
      (appointment) =>
        appointment.seriesId === authoritativeActiveSeries?.id &&
        appointment.kind === 'coaching' &&
        appointment.bookingStatus === 'confirmed',
    ) ?? [];
  const now = Date.now();
  const actionableSeriesOccurrences = seriesOccurrences.filter(
    (appointment) =>
      appointment.lifecycleStatus === 'scheduled' &&
      appointment.actionCapabilities.reschedule.allowed &&
      appointment.actionCapabilities.reschedule.scopes.includes(
        'this_and_following',
      ),
  );
  const eligibleFutureSeriesAppointment = actionableSeriesOccurrences.find(
    (appointment) =>
      Date.parse(appointment.currentSchedule.startAt) - now >
      FORTY_EIGHT_HOURS_MS,
  );
  const nextFutureSeriesAppointment = actionableSeriesOccurrences.find(
    (appointment) => Date.parse(appointment.currentSchedule.startAt) > now,
  );
  const latestHistoricalSeriesAppointment = seriesOccurrences
    .filter(
      (appointment) => Date.parse(appointment.currentSchedule.startAt) <= now,
    )
    .at(-1);
  const resolvedSeriesAppointment =
    eligibleFutureSeriesAppointment ??
    nextFutureSeriesAppointment ??
    latestHistoricalSeriesAppointment;
  const isActiveSeriesAppointmentsPending = Boolean(
    authoritativeActiveSeries &&
    (activeSeriesAppointments.isLoading ||
      (activeSeriesAppointments.isFetching &&
        !activeSeriesAppointments.currentData)),
  );
  const nativeActiveSeriesAppointmentId =
    retainedFutureSeriesCommand?.appointmentId ??
    (retainedFutureSeriesSelection.kind === 'none' &&
    !isActiveSeriesAppointmentsPending
      ? (resolvedSeriesAppointment?.id ??
        (requiresActiveSeries
          ? undefined
          : authoritativeActiveSeries?.nextAppointmentId))
      : undefined);
  const activeSeriesAppointment = useGetAppointmentQuery(
    nativeActiveSeriesAppointmentId && selectedTransport === 'rails'
      ? { appointmentId: nativeActiveSeriesAppointmentId, userId: user.id }
      : skipToken,
  );
  const momentsActiveSeriesAppointment =
    useGetMomentsParticipantAppointmentQuery(
      nativeActiveSeriesAppointmentId && selectedTransport === 'moments'
        ? { appointmentId: nativeActiveSeriesAppointmentId }
        : skipToken,
    );
  const selectedActiveSeriesAppointment =
    selectedTransport === 'moments'
      ? momentsActiveSeriesAppointment
      : activeSeriesAppointment;
  const authoritativeSeatType =
    authoritativeSchedulingContext?.seatType ??
    (data?.seatType === 'executive' ? 'executive' : 'leadership');
  const memberEmail = data?.member.email ?? user.email;
  const [bookAppointment, bookingResult] = useBookAppointmentMutation();
  const [bookMomentsAppointment, momentsBookingResult] =
    useBookMomentsParticipantAppointmentMutation();
  const [getAppointment] = useLazyGetAppointmentQuery();
  const [getMomentsAppointment] =
    useLazyGetMomentsParticipantAppointmentQuery();
  const [getAppointments] = useLazyGetAppointmentsQuery();
  const confirmationCandidateFromReconciledSeries = async (
    context: BrowserSchedulingContext | ParticipantSchedulingContext,
    attempt: BookingAttempt,
    calendarEventTitle?: string,
  ) => {
    const activeSeries = matchingActiveSeries(context, attempt);
    if (!activeSeries || !context.seatType) return null;
    const page = await getAppointments({
      ...(isAdminMode ? { memberId: effectiveMemberId } : {}),
      seriesId: activeSeries.id,
      userId: user.id,
    }).unwrap();
    const appointments = page.appointments.filter(
      (appointment) =>
        appointment.seriesId === activeSeries.id &&
        appointment.bookingStatus === 'confirmed' &&
        Date.parse(appointment.currentSchedule.startAt) >=
          Date.parse(attempt.firstStart),
    );
    return confirmationCandidateFromMutation({
      cadenceWeeks: attempt.cadenceWeeks,
      calendarEventTitle,
      durationMinutes: context.durationMinutes,
      expectedCoachPersonId: attempt.coachPersonId,
      expectedFirstStart: attempt.firstStart,
      expectedMemberPersonId: attempt.memberPersonId,
      expectedOccurrenceCount: attempt.occurrenceCount,
      expectedSeatType: context.seatType,
      memberEmail,
      result: {
        appointments,
        contractVersion: 'appointment-mutation.v1',
        operationId: 'series-reconciliation',
        rejection: null,
        status: 'confirmed',
      },
    });
  };
  let operationPollingInterval = 0;
  if (pendingOperation && !operationPollingStopped) {
    operationPollingInterval = Math.max(
      1000,
      (pendingOperation.retryAfterSeconds ?? 5) * 1000,
    );
  }
  const operationQuery = useGetAppointmentOperationQuery(
    pendingOperation && (storedAttempt?.transport ?? 'rails') === 'rails'
      ? {
          browserContractMarker: EXACT_APPOINTMENT_CONTRACT,
          operationId: pendingOperation.operationId,
        }
      : skipToken,
    {
      pollingInterval: operationPollingInterval,
    },
  );
  const momentsOperationQuery =
    useGetMomentsParticipantAppointmentOperationQuery(
      pendingOperation && storedAttempt?.transport === 'moments'
        ? { operationId: pendingOperation.operationId }
        : skipToken,
      { pollingInterval: operationPollingInterval },
    );
  const momentsOperationData = momentsOperationQuery.currentData
    ? {
        ...momentsOperationQuery.currentData,
        rejection: participantRejection(momentsOperationQuery.currentData),
      }
    : undefined;
  const operationData =
    storedAttempt?.transport === 'moments'
      ? momentsOperationData
      : operationQuery.currentData;
  const operationError =
    storedAttempt?.transport === 'moments'
      ? momentsOperationQuery.error
      : operationQuery.error;

  const invalidateConfirmedAttempt = (
    attempt: StoredBookingAttempt,
    includeSchedulingContext: boolean,
    token: object,
  ) => {
    if (
      attempt.confirmationInvalidated ||
      !isCurrentRouteInstance(token) ||
      attempt.routeKey !== routeKey
    ) {
      return attempt;
    }
    const invalidatedAttempt = {
      ...attempt,
      confirmationInvalidated: true,
    };
    if (!saveBookingAttempt(invalidatedAttempt)) return attempt;
    setStoredAttempt(invalidatedAttempt);
    dispatch(
      appointmentApi.util.invalidateTags([
        ...(includeSchedulingContext
          ? [{ id: 'SCHEDULING_CONTEXT', type: 'Appointment' as const }]
          : []),
        { id: 'LIST', type: 'Appointment' as const },
        'Session',
        'User',
      ]),
    );
    return invalidatedAttempt;
  };

  useEffect(() => {
    if (pendingOperation && appointmentUpgradeGuardSpent(operationError)) {
      setOperationPollingStopped(true);
    }
  }, [operationError, pendingOperation]);

  useEffect(() => {
    const token = routeInstanceToken.current;
    if (
      storedAttempt?.status !== 'confirmed' ||
      !storedAttempt.confirmationCandidate ||
      storedAttempt.routeKey !== routeKey ||
      !isCurrentRouteInstance(token)
    ) {
      return;
    }
    const shouldConsumeReceipt =
      restoredConfirmationReceipt.current === storedAttempt.idempotencyKey;
    if (storedAttempt.confirmationInvalidated) {
      if (shouldConsumeReceipt) {
        clearBookingAttempt(routeKey);
        restoredConfirmationReceipt.current = null;
      }
      return;
    }
    const invalidatedAttempt = {
      ...storedAttempt,
      confirmationInvalidated: true,
    };
    if (!saveBookingAttempt(invalidatedAttempt)) return;
    setStoredAttempt(invalidatedAttempt);
    dispatch(
      appointmentApi.util.invalidateTags([
        { id: 'SCHEDULING_CONTEXT', type: 'Appointment' },
        { id: 'LIST', type: 'Appointment' },
        'Session',
        'User',
      ]),
    );
    if (shouldConsumeReceipt) {
      clearBookingAttempt(routeKey);
      restoredConfirmationReceipt.current = null;
    }
  }, [dispatch, storedAttempt]);

  const finishRejectedAttempt = async ({
    attempt,
    detailAppointmentIds,
    message,
    token,
  }: {
    attempt: StoredBookingAttempt;
    detailAppointmentIds: string[];
    message: string;
    token: object;
  }) => {
    if (!isCurrentRouteInstance(token)) return;
    setPendingOperation(null);
    try {
      await refetchAuthoritativeSchedulingContext().unwrap();
    } catch {
      // Rejection remains authoritative even when current availability fails.
    }
    if (!isCurrentRouteInstance(token)) return;
    await Promise.allSettled(
      detailAppointmentIds.map(async (appointmentId) => {
        const detailRequest =
          attempt.transport === 'moments'
            ? getMomentsAppointment({ appointmentId })
            : getAppointment({ appointmentId });
        try {
          await detailRequest.unwrap();
        } finally {
          detailRequest.unsubscribe();
        }
      }),
    );
    if (
      !isCurrentRouteInstance(token) ||
      attemptGeneration.current !== attempt.generation ||
      routeKey !== attempt.routeKey
    ) {
      return;
    }
    const rejectedAttempt: StoredBookingAttempt = {
      ...attempt,
      errorMessage: message,
      rejectedCanonicalDigest: attempt.canonicalDigest,
      status: 'rejected',
    };
    saveBookingAttempt(rejectedAttempt);
    setStoredAttempt(rejectedAttempt);
    releaseSubmissionClaim();
    setBookingError(message);
    setAttemptPhase('failed');
  };

  // Engagement-end block screens take precedence over the calendar UI.
  const errCode = skipBookingDetailsQuery ? undefined : errorCode(getError);
  const selectedDay = data?.availability.days.find(
    (day) => day.date === selectedDate,
  );
  const firstDaySessionCount = data?.availability.days.find(
    (day) => typeof day.sessionCount === 'number',
  )?.sessionCount;
  const currentSessionCount =
    selectedDay?.sessionCount ??
    data?.sessionCount ??
    firstDaySessionCount ??
    0;

  // eslint-disable-next-line complexity -- Terminal recovery validates receipt ownership and bounded detail reconciliation.
  useEffect(() => {
    const token = routeInstanceToken.current;
    if (!pendingOperation || !operationData) return;
    if (
      !isCurrentRouteInstance(token) ||
      operationData.id !== pendingOperation.operationId
    ) {
      return;
    }
    if (
      attemptGeneration.current !== pendingOperation.generation ||
      storedAttempt?.generation !== pendingOperation.generation ||
      storedAttempt.routeKey !== pendingOperation.routeKey ||
      storedAttempt.pendingOperationId !== pendingOperation.operationId
    ) {
      return;
    }
    if (operationData.status === 'pending') {
      if (
        pendingOperation.retryAfterSeconds !== operationData.retryAfterSeconds
      ) {
        setPendingOperation({
          ...pendingOperation,
          retryAfterSeconds: operationData.retryAfterSeconds,
        });
      }
      return;
    }
    if (operationData.status === 'confirmed') {
      const isSnapshotFreeDirectAttempt =
        storedAttempt.transport === 'moments' &&
        storedAttempt.confirmationCandidate === null &&
        storedAttempt.pendingAppointmentIds.length === 0;
      if (
        !isSnapshotFreeDirectAttempt &&
        !sameAppointmentIds(
          operationData.appointmentIds,
          storedAttempt.pendingAppointmentIds,
        )
      ) {
        const errorMessage = ownedBookingErrorMessage(
          pendingOperation.operationId,
        );
        const errorAttempt: StoredBookingAttempt = {
          ...storedAttempt,
          confirmationCandidate: null,
          errorMessage,
          status: 'error',
        };
        saveBookingAttempt(errorAttempt);
        setStoredAttempt(errorAttempt);
        setPendingOperation(null);
        setBookingError(errorMessage);
        setAttemptPhase('failed');
        return;
      }
      const candidate = storedAttempt.confirmationCandidate;
      if (!candidate) {
        if (isSnapshotFreeDirectAttempt) {
          if (
            confirmationRecoveryOperation.current ===
            pendingOperation.operationId
          ) {
            return;
          }
          confirmationRecoveryOperation.current = pendingOperation.operationId;
          const retryConfirmationDetails = () => {
            if (
              !isCurrentRouteInstance(token) ||
              attemptGeneration.current !== storedAttempt.generation ||
              routeKey !== storedAttempt.routeKey
            ) {
              return;
            }
            const nextAttempt = confirmationDetailAttempt + 1;
            if (nextAttempt > MAX_CONFIRMATION_DETAIL_RETRIES) {
              const reviewAttempt: StoredBookingAttempt = {
                ...storedAttempt,
                errorMessage: CONFIRMATION_DETAIL_REVIEW_MESSAGE,
                status: 'error',
              };
              if (!transitionBookingAttempt(storedAttempt, reviewAttempt)) {
                return;
              }
              setStoredAttempt(reviewAttempt);
              setPendingOperation(null);
              setBookingError(CONFIRMATION_DETAIL_REVIEW_MESSAGE);
              setAttemptPhase('failed');
              return;
            }
            confirmationDetailRetryTimer.current = setTimeout(() => {
              confirmationDetailRetryTimer.current = null;
              confirmationRecoveryOperation.current = null;
              setConfirmationDetailAttempt(nextAttempt);
            }, CONFIRMATION_DETAIL_RETRY_MS);
          };
          Promise.all(
            operationData.appointmentIds.map(async (appointmentId) => {
              const detailRequest = getMomentsAppointment({ appointmentId });
              try {
                return await detailRequest.unwrap();
              } finally {
                detailRequest.unsubscribe();
              }
            }),
          )
            .then((appointments) => {
              if (
                !isCurrentRouteInstance(token) ||
                attemptGeneration.current !== storedAttempt.generation ||
                routeKey !== storedAttempt.routeKey
              ) {
                return;
              }
              const hydratedCandidate =
                confirmationCandidateFromStoredAttemptDetails({
                  appointments,
                  attempt: storedAttempt,
                  memberEmail,
                });
              if (!hydratedCandidate) {
                retryConfirmationDetails();
                return;
              }
              const confirmedAttempt: StoredBookingAttempt = {
                ...storedAttempt,
                confirmationCandidate: hydratedCandidate,
                pendingAppointmentIds: hydratedCandidate.appointmentIds,
                status: 'confirmed',
              };
              if (!transitionBookingAttempt(storedAttempt, confirmedAttempt)) {
                confirmationRecoveryOperation.current = null;
                return;
              }
              setConfirmationDetailAttempt(0);
              invalidateConfirmedAttempt(confirmedAttempt, true, token);
              setConfirmationCandidate(hydratedCandidate);
              setConfirmedSeries({
                firstStart: hydratedCandidate.firstStart,
                intervalWeeks: hydratedCandidate.cadenceWeeks,
                occurrenceCount: hydratedCandidate.occurrenceCount,
                seriesId: hydratedCandidate.seriesId,
                status: 'confirmed',
              });
              setConfirmationTimezone(hydratedCandidate.timezone);
              setAttemptPhase('confirmed');
              setPendingOperation(null);
            })
            .catch(retryConfirmationDetails);
          return;
        }
        if (
          confirmationRecoveryOperation.current === pendingOperation.operationId
        ) {
          return;
        }
        confirmationRecoveryOperation.current = pendingOperation.operationId;
        setPendingOperation(null);
        const reconcilingAttempt: StoredBookingAttempt = {
          ...storedAttempt,
          status: 'reconciling',
        };
        saveBookingAttempt(reconcilingAttempt);
        setStoredAttempt(reconcilingAttempt);
        setAttemptPhase('reconciling');
        refetchAuthoritativeSchedulingContext()
          .unwrap()
          .then(async (reconciledContext) => {
            if (
              !isCurrentRouteInstance(token) ||
              attemptGeneration.current !== reconcilingAttempt.generation ||
              routeKey !== reconcilingAttempt.routeKey
            ) {
              return;
            }
            const reconciliationAttempt = {
              cadenceWeeks: 2,
              coachPersonId: authoritativeCoachPersonId ?? '',
              firstStart: pendingOperation.startAt,
              generation: reconcilingAttempt.generation,
              idempotencyKey: reconcilingAttempt.idempotencyKey,
              memberPersonId: authoritativeMemberPersonId ?? '',
              occurrenceCount: pendingOperation.occurrenceCount,
              routeKey: reconcilingAttempt.routeKey,
            };
            const reconciledCandidate =
              await confirmationCandidateFromReconciledSeries(
                reconciledContext,
                reconciliationAttempt,
                reconcilingAttempt.calendarEventTitle,
              );
            if (
              !isCurrentRouteInstance(token) ||
              attemptGeneration.current !== reconcilingAttempt.generation ||
              routeKey !== reconcilingAttempt.routeKey
            ) {
              return;
            }
            if (!reconciledCandidate) {
              const errorAttempt: StoredBookingAttempt = {
                ...reconcilingAttempt,
                status: 'error',
              };
              saveBookingAttempt(errorAttempt);
              setStoredAttempt(errorAttempt);
              setBookingError(
                'We could not safely confirm this booking. Please refresh to reconcile its status.',
              );
              setAttemptPhase('failed');
              return;
            }
            const confirmedAttempt: StoredBookingAttempt = {
              ...reconcilingAttempt,
              confirmationCandidate: reconciledCandidate,
              pendingAppointmentIds: reconciledCandidate.appointmentIds,
              status: 'confirmed',
            };
            saveBookingAttempt(confirmedAttempt);
            invalidateConfirmedAttempt(confirmedAttempt, true, token);
            setConfirmationCandidate(reconciledCandidate);
            setConfirmedSeries({
              firstStart: reconciledCandidate.firstStart,
              intervalWeeks: reconciledCandidate.cadenceWeeks,
              occurrenceCount: reconciledCandidate.occurrenceCount,
              seriesId: reconciledCandidate.seriesId,
              status: 'confirmed',
            });
            setConfirmationTimezone(reconciledCandidate.timezone);
            setAttemptPhase('confirmed');
          })
          .catch(() => {
            if (
              !isCurrentRouteInstance(token) ||
              attemptGeneration.current !== reconcilingAttempt.generation ||
              routeKey !== reconcilingAttempt.routeKey
            ) {
              return;
            }
            const errorAttempt: StoredBookingAttempt = {
              ...reconcilingAttempt,
              status: 'error',
            };
            saveBookingAttempt(errorAttempt);
            setStoredAttempt(errorAttempt);
            setBookingError(
              'We could not safely confirm this booking. Please refresh to reconcile its status.',
            );
            setAttemptPhase('failed');
          });
        return;
      }
      const confirmedAttempt: StoredBookingAttempt = {
        ...storedAttempt,
        status: 'confirmed',
      };
      saveBookingAttempt(confirmedAttempt);
      invalidateConfirmedAttempt(confirmedAttempt, true, token);
      setConfirmationCandidate(candidate);
      setConfirmedSeries({
        firstStart: candidate.firstStart,
        intervalWeeks: candidate.cadenceWeeks,
        occurrenceCount: candidate.occurrenceCount,
        seriesId: candidate.seriesId,
        status: 'confirmed',
      });
      setConfirmationTimezone(candidate.timezone);
      setAttemptPhase('confirmed');
      setPendingOperation(null);
      return;
    }
    if (rejectionRecoveryOperation.current === pendingOperation.operationId) {
      return;
    }
    rejectionRecoveryOperation.current = pendingOperation.operationId;
    finishRejectedAttempt({
      attempt: storedAttempt,
      detailAppointmentIds: storedAttempt.pendingAppointmentIds,
      message: appointmentRejectionMessage(operationData.rejection, 'booking'),
      token,
    }).catch(() => undefined);
  }, [
    confirmationDetailAttempt,
    operationData,
    pendingOperation,
    storedAttempt,
  ]);

  if (!isUserLoading && pendingOperation && operationPollingStopped) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            Booking confirmation is paused
          </Typography>
          <Typography className="text-cement-400">
            {APPOINTMENT_CONFIRMATION_PAUSED_MESSAGE}
          </Typography>
        </Paper>
      </main>
    );
  }

  // MEN-4915: reached ONLY when the pending attempt could not be persisted.
  // This is no longer a latency gate -- the confirmation above handles every
  // normal pending case. Here we genuinely cannot promise the booking survives
  // a refresh, so the member gets a support reference and the navigation that
  // would lose it stays hidden.
  if (!isUserLoading && pendingOperation && pendingStorageUnavailable) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          data-testid={PENDING_APPOINTMENT_PANEL_TEST_ID}
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            Pending Appointment
          </Typography>
          <Typography className="text-cement-400">
            {appointmentPendingMessage('booking')}
          </Typography>
          {/* This panel is now reached only when the attempt could not be
              persisted, so the storage warning is unconditional here. The
              "Back to Sessions" branch this replaced was unreachable and is in
              git history; the Sessions page resolves a pending attempt on its
              own via findViewerPendingBookingAttempt. */}
          <Typography className="text-cement-400">
            Keep this page open — we could not save this booking on this device.
            If you need help before it confirms, give support this reference:{' '}
            {pendingOperation.operationId}
          </Typography>
        </Paper>
      </main>
    );
  }

  // MEN-4915: the confirmation is checked FIRST. Whenever the admission
  // response gave us a series, the member sees their booking; the pending
  // panel below is retained but no longer reached on this path. It existed
  // only to cover the Google-settling latency that used to make the wait
  // visible (~14.6s of serial occurrence patching plus a 2-minute first
  // retry, measured on staging).
  // `attemptPhase !== 'failed'` is load-bearing: a rejection or a failed
  // reconciliation sets that phase and a booking error WITHOUT clearing
  // confirmedSeries, so without this guard the confirmation would render on
  // top of a rejected attempt and tell the member they are booked when they
  // are not.
  if (
    !isUserLoading &&
    confirmedSeries &&
    confirmationCandidate &&
    attemptPhase !== 'failed'
  ) {
    const confirmationCoachName = confirmationCandidate.coach.displayName;
    const [confirmationCoachFirstName = '', ...confirmationCoachLastNameParts] =
      confirmationCoachName.split(' ');
    return (
      <BookingConfirmation
        calendarEventTitle={confirmationCandidate.calendarEventTitle}
        coachAvatarUrl={confirmationCandidate.coach.avatarUrl ?? undefined}
        coachFirstName={confirmationCoachFirstName}
        coachLastName={confirmationCoachLastNameParts.join(' ')}
        coachName={confirmationCoachName}
        durationMinutes={confirmationCandidate.durationMinutes}
        memberEmail={confirmationCandidate.memberEmail}
        providerPending={Boolean(pendingOperation)}
        seatTypeTitle={seatTitle(confirmationCandidate.seatType)}
        series={confirmedSeries}
        timezone={confirmationTimezone ?? confirmationCandidate.timezone}
        onDone={() => {
          // clearBookingAttempt's `expectedStatus` defaults to 'confirmed', so
          // a still-pending receipt survives this and Sessions/Upcoming can
          // resume it through findViewerPendingBookingAttempt.
          clearBookingAttempt(routeKey);
          setStoredAttempt(null);
          // MEN-4915 (PR #1664 review): the observer lives in Upcoming, but
          // returnPath is '/m', whose index renders Home. Leaving while the
          // operation is still pending would strand it there until the member
          // happened to visit Upcoming, losing the cache refresh and any later
          // rejection or needs_reconciliation. Send them to the observer.
          navigate(
            pendingOperation && !isAdminMode
              ? '/m/sessions/upcoming'
              : returnPath,
          );
        }}
      />
    );
  }

  if (
    !isUserLoading &&
    storedAttempt &&
    ((storedAttempt.status === 'error' &&
      (storedAttempt.pendingOperationId !== null ||
        Boolean(storedAttempt.errorMessage))) ||
      storedAttempt.status === 'submitting' ||
      storedAttempt.status === 'reconciling')
  ) {
    const ownsActiveAttempt =
      currentAttempt.current?.routeKey === storedAttempt.routeKey &&
      currentAttempt.current.generation === storedAttempt.generation &&
      currentAttempt.current.idempotencyKey === storedAttempt.idempotencyKey;
    const ownsConfirmationRecovery =
      confirmationRecoveryOperation.current !== null &&
      confirmationRecoveryOperation.current ===
        storedAttempt.pendingOperationId;
    const isSubmitting =
      storedAttempt.status === 'submitting' && ownsActiveAttempt;
    const isReconciling =
      storedAttempt.status === 'reconciling' &&
      (ownsActiveAttempt || ownsConfirmationRecovery);
    let attemptHeading = 'Booking outcome needs review';
    let attemptMessage =
      storedAttempt.errorMessage ??
      (storedAttempt.pendingOperationId
        ? ownedBookingErrorMessage(storedAttempt.pendingOperationId)
        : 'We could not safely confirm this booking attempt.');
    if (isSubmitting) {
      attemptHeading = 'Booking in progress';
      attemptMessage =
        'We are booking this time with your coach. Please keep this page open.';
    } else if (isReconciling) {
      attemptHeading = 'Confirming your booking';
      attemptMessage =
        'We are checking this booking attempt. Please keep this page open.';
    }
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            {attemptHeading}
          </Typography>
          <Typography className="text-cement-400">{attemptMessage}</Typography>
          {!isSubmitting &&
            !isReconciling &&
            !storedAttempt.pendingOperationId && (
              <Typography className="text-cement-400">
                Reference {storedAttempt.idempotencyKey} before booking another
                time.
              </Typography>
            )}
        </Paper>
      </main>
    );
  }

  const schedulingContextUnavailable =
    isLoading ||
    momentsContextQuery.isLoading ||
    (isFetching && !data) ||
    (momentsContextQuery.isFetching && !data) ||
    Boolean(errCode) ||
    Boolean(momentsContextQuery.error) ||
    routeDecision.state === 'unavailable' ||
    !data;
  if (
    !isUserLoading &&
    storedAttempt?.status === 'rejected' &&
    bookingError &&
    schedulingContextUnavailable
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            Booking not confirmed
          </Typography>
          <Typography className="text-cement-400">{bookingError}</Typography>
        </Paper>
      </main>
    );
  }

  if (errCode === 'engagement_end_missing') {
    return (
      <BlockScreen
        body="Your engagement length isn't set yet. Please contact your coach to confirm your contract dates before scheduling a series."
        title="Set up your engagement to schedule"
        onDone={() => navigate(returnPath)}
      />
    );
  }
  if (errCode === 'engagement_ended') {
    return (
      <BlockScreen
        body="Your current engagement has ended. Reach out to your coach if you'd like to discuss a renewal."
        title="Your engagement has ended"
        onDone={() => navigate(returnPath)}
      />
    );
  }

  if (
    isUserLoading ||
    isLoading ||
    momentsContextQuery.isLoading ||
    (isFetching && !data) ||
    (momentsContextQuery.isFetching && !data) ||
    routeDecision.state === 'loading'
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200">
        <div className="flex items-center gap-3">
          <CircularProgress size={20} />
          <Typography>Loading booking details...</Typography>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            We could not load this booking link.
          </Typography>
          <Typography className="text-cement-400">
            Make sure you are signed in with the member account assigned to this
            coach.
          </Typography>
        </Paper>
      </main>
    );
  }

  if (
    !isAdminMode &&
    requiresActiveSeries &&
    !authoritativeActiveSeries &&
    retainedFutureSeriesSelection.kind === 'none'
  ) {
    return (
      <BlockScreen
        body="You no longer have an active coaching series to update."
        title="Coaching series unavailable"
        onDone={() => navigate(returnPath)}
      />
    );
  }

  if (
    !isAdminMode &&
    (data.hasActiveSeries || retainedFutureSeriesSelection.kind !== 'none') &&
    attemptPhase === 'idle'
  ) {
    if (retainedFutureSeriesSelection.kind === 'ambiguous') {
      return (
        <BlockScreen
          body="We found more than one saved series update. Contact support before scheduling another change."
          title="Saved reschedule needs review"
          onDone={() => navigate(returnPath)}
        />
      );
    }
    if (isActiveSeriesAppointmentsPending) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-sunny-200">
          <div className="flex items-center gap-3">
            <CircularProgress size={20} />
            <Typography>Loading series appointments...</Typography>
          </div>
        </main>
      );
    }
    if (activeSeriesAppointments.isError) {
      return (
        <BlockScreen
          body="We could not load the appointments for this coaching series."
          title="Coaching series unavailable"
          onDone={() => navigate(returnPath)}
        />
      );
    }
    if (!nativeActiveSeriesAppointmentId) {
      return (
        <BlockScreen
          body={
            requiresActiveSeries
              ? 'We could not find an appointment to update for this coaching series.'
              : 'You already have an active bi-weekly series with this coach.'
          }
          title={
            requiresActiveSeries
              ? 'Coaching series unavailable'
              : 'Coaching series already scheduled'
          }
          onDone={() => navigate(returnPath)}
        />
      );
    }
    if (
      selectedActiveSeriesAppointment.isLoading ||
      (selectedActiveSeriesAppointment.isFetching &&
        !selectedActiveSeriesAppointment.currentData)
    ) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-sunny-200">
          <div className="flex items-center gap-3">
            <CircularProgress size={20} />
            <Typography>Loading appointment details...</Typography>
          </div>
        </main>
      );
    }
    const isRetainedRecovery =
      retainedFutureSeriesSelection.kind === 'selected';
    const expectedSeriesId = isRetainedRecovery
      ? retainedFutureSeriesCommand?.seriesId
      : authoritativeActiveSeries?.id;
    const selectedSeriesOccurrenceIsPast = Boolean(
      selectedActiveSeriesAppointment.currentData &&
      resolvedSeriesAppointment?.id ===
        selectedActiveSeriesAppointment.currentData.id &&
      Date.parse(
        selectedActiveSeriesAppointment.currentData.currentSchedule.startAt,
      ) <= Date.now(),
    );
    if (
      !selectedActiveSeriesAppointment.currentData ||
      selectedActiveSeriesAppointment.currentData.id !==
        nativeActiveSeriesAppointmentId ||
      selectedActiveSeriesAppointment.currentData.kind !== 'coaching' ||
      selectedActiveSeriesAppointment.currentData.seriesId !==
        expectedSeriesId ||
      (isRetainedRecovery &&
        (selectedActiveSeriesAppointment.currentData.expectedIdentities.coach
          .personId !== authoritativeCoachPersonId ||
          selectedActiveSeriesAppointment.currentData.expectedIdentities.member
            .personId !== authoritativeMemberPersonId)) ||
      (!isRetainedRecovery &&
        !selectedSeriesOccurrenceIsPast &&
        (!selectedActiveSeriesAppointment.currentData.actionCapabilities
          .reschedule.allowed ||
          !selectedActiveSeriesAppointment.currentData.actionCapabilities.reschedule.scopes.includes(
            'this_and_following',
          ))) ||
      selectedActiveSeriesAppointment.isError
    ) {
      return (
        <BlockScreen
          body="We could not load the appointment for this coaching series."
          title="Coaching series unavailable"
          onDone={() => navigate(returnPath)}
        />
      );
    }
    return (
      <MemberRescheduleSession
        key={selectedActiveSeriesAppointment.currentData.id}
        close={() => navigate('/m/sessions/upcoming')}
        mode="futureSeries"
        session={appointmentActionSession(
          selectedActiveSeriesAppointment.currentData,
        )}
      />
    );
  }

  const [coachFirstName = '', ...coachLastNameParts] =
    data.coach.fullName?.split(' ') ?? [];
  const coachLastName = coachLastNameParts.join(' ');
  const selectedDayDate = selectedDay?.date ?? '';

  const slotStillValid =
    selectedSlot !== null &&
    selectedDay?.slots.some(
      (s) => slotStartTime(s) === slotStartTime(selectedSlot),
    );
  const validatedSlot = slotStillValid ? selectedSlot : null;
  // MEN-5428: read conflicts from the current context, not the click-time slot
  // object, so a refetch can never show a stale note.
  const selectedCoachConflictStarts =
    (validatedSlot &&
      selectedDay?.slots.find(
        (slot) => slotStartTime(slot) === slotStartTime(validatedSlot),
      )?.coachConflictStarts) ??
    [];
  const selectedCanonicalDigest = validatedSlot
    ? canonicalBookingDigest({
        cadenceWeeks: 2,
        coachId,
        memberId: effectiveMemberId,
        occurrenceCount: currentSessionCount,
        startAt: slotStartTime(validatedSlot),
      })
    : null;
  const rejectedSelectionBlocked = Boolean(
    selectedCanonicalDigest &&
    storedAttempt?.rejectedCanonicalDigest === selectedCanonicalDigest,
  );

  const selectedDateLabel = mentoCalBookingDayHeading(selectedDay?.date ?? '');
  const canShowMore = visibleWeeks < MAX_VISIBLE_WEEKS;
  const calendarDays = data.availability.days.map((d) => ({
    date: d.date,
    enabled: d.enabled,
  }));
  const seatTypeTitle = seatTitle(data.seatType);
  // Prefer the booking-context member (the person being booked for — correct
  // when an EA/Ops books on behalf) and fall back to the logged-in member.
  const availableSlots = normalizeMemberBookingSlots(selectedDay?.slots ?? []);
  let bookingButtonLabel = isAdminMode ? 'Book' : 'Confirm Booking';
  if (bookingResult.isLoading || momentsBookingResult.isLoading)
    bookingButtonLabel = 'Booking...';
  if (attemptPhase === 'reconciling') bookingButtonLabel = 'Confirming...';

  const onSelectDay = (date: string) => {
    if (attemptPhase === 'failed') setBookingError('');
    setSelectedDate(date);
    setSelectedSlot(null);
    setIsTimeMenuOpen(false);
  };

  const onShowMore = () => {
    setVisibleWeeks((current) => Math.min(MAX_VISIBLE_WEEKS, current + 1));
  };

  // eslint-disable-next-line complexity -- Submission coordinates durable attempt, rejection, polling, and reconciliation transitions.
  const onSchedule = async () => {
    const startTime = validatedSlot ? slotStartTime(validatedSlot) : '';
    if (
      !startTime ||
      getError ||
      momentsContextQuery.error ||
      !selectedTransport
    )
      return;

    if (hasAdminCoachConflict) return;
    if (rejectedSelectionBlocked || !selectedCanonicalDigest) return;
    if (!authoritativeCoachPersonId || !authoritativeMemberPersonId) return;

    const token = routeInstanceToken.current;
    if (!isCurrentRouteInstance(token)) return;
    if (submissionClaimed.current) return;
    submissionClaimed.current = true;
    const generation = attemptGeneration.current + 1;
    attemptGeneration.current = generation;
    rejectionRecoveryOperation.current = null;
    const canonicalDigest = selectedCanonicalDigest;
    const isExactRetry =
      storedAttempt?.routeKey === routeKey &&
      storedAttempt.canonicalDigest === canonicalDigest &&
      (storedAttempt.status === 'error' ||
        storedAttempt.status === 'reconciling' ||
        storedAttempt.status === 'submitting');
    const idempotencyKey = isExactRetry
      ? storedAttempt.idempotencyKey
      : `member-biweekly-booking:member:${effectiveMemberId}:attempt:${window.crypto.randomUUID()}`;
    let bookContractMetadata: Partial<
      Pick<
        StoredBookingAttempt,
        'bookContractVersion' | 'browserContractMarker' | 'calendarEventTitle'
      >
    > = {};
    if (isExactRetry) {
      if (storedAttempt.bookContractVersion === 'appointment-book.v2') {
        bookContractMetadata = {
          bookContractVersion: storedAttempt.bookContractVersion,
          browserContractMarker: EXACT_APPOINTMENT_CONTRACT,
          calendarEventTitle: storedAttempt.calendarEventTitle,
        };
      }
    } else if (
      authoritativeSchedulingContext?.contractVersion ===
      'appointment-scheduling-context.v2'
    ) {
      bookContractMetadata = {
        bookContractVersion: 'appointment-book.v2',
        browserContractMarker: EXACT_APPOINTMENT_CONTRACT,
        calendarEventTitle: authoritativeSchedulingContext.calendarEventTitle,
      };
    }
    const attempt: BookingAttempt = {
      cadenceWeeks: 2,
      coachPersonId: authoritativeCoachPersonId,
      firstStart: startTime,
      generation,
      idempotencyKey,
      memberPersonId: authoritativeMemberPersonId,
      occurrenceCount: currentSessionCount,
      routeKey,
    };
    const attemptTransport = isExactRetry
      ? (storedAttempt.transport ?? 'rails')
      : selectedTransport;
    const submittedAttempt: StoredBookingAttempt = {
      ...bookContractMetadata,
      canonicalDigest,
      confirmationCandidate: null,
      ...(attemptTransport === 'moments'
        ? {
            confirmationExpectation: {
              cadenceWeeks: 2,
              coachPersonId: authoritativeCoachPersonId,
              firstStart: startTime,
              memberPersonId: authoritativeMemberPersonId,
              occurrenceCount: currentSessionCount,
              seatType: authoritativeSeatType,
            },
          }
        : {}),
      confirmationInvalidated: false,
      errorMessage: null,
      generation,
      idempotencyKey,
      pendingAppointmentIds: [],
      pendingOperationId: null,
      rejectedCanonicalDigest: null,
      routeKey: attempt.routeKey,
      status: 'submitting',
      transport: attemptTransport,
    };
    if (!saveBookingAttempt(submittedAttempt)) {
      releaseSubmissionClaim();
      setBookingError(
        'We could not safely retain this booking attempt. Resolve an existing booking before trying again.',
      );
      setAttemptPhase('failed');
      return;
    }
    currentAttempt.current = attempt;
    setAttemptPhase('submitting');
    setStoredAttempt(submittedAttempt);

    try {
      setBookingError('');
      setPendingOperation(null);
      const response = await (
        submittedAttempt.transport === 'moments'
          ? bookMomentsAppointment({
              bookingPattern: 'series',
              counterpart: { externalId: coachId, provider: 'legacy_app' },
              idempotencyKey,
              kind: 'coaching',
              startAt: startTime,
              viewerTimezone,
            })
          : bookAppointment({
              appointmentCount: currentSessionCount,
              browserContractMarker:
                submittedAttempt.browserContractMarker ??
                PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
              cadenceWeeks: 2,
              coachId,
              ...(submittedAttempt.bookContractVersion === 'appointment-book.v2'
                ? {
                    contractVersion: 'appointment-book.v2' as const,
                    expectedCalendarEventTitle:
                      submittedAttempt.calendarEventTitle!,
                  }
                : {}),
              idempotencyKey,
              kind: 'coaching',
              memberId,
              startAt: startTime,
              viewerTimezone,
            })
      ).unwrap();
      if (response.status === 'pending') {
        const candidate = confirmationCandidateFromMutation({
          cadenceWeeks: 2,
          calendarEventTitle: submittedAttempt.calendarEventTitle,
          durationMinutes: authoritativeSchedulingContext?.durationMinutes,
          expectedCoachPersonId: authoritativeCoachPersonId,
          expectedFirstStart: startTime,
          expectedMemberPersonId: authoritativeMemberPersonId,
          expectedOccurrenceCount: currentSessionCount,
          expectedSeatType: authoritativeSeatType,
          memberEmail,
          result: response,
        });
        if (!candidate) {
          if (
            submittedAttempt.transport === 'moments' &&
            response.appointments.length === 0
          ) {
            const pendingAttempt: StoredBookingAttempt = {
              ...submittedAttempt,
              pendingOperationId: response.operationId,
              retryAfterSeconds: response.retryAfterSeconds ?? null,
              status: 'pending',
            };
            const pendingPersisted = transitionBookingAttempt(
              submittedAttempt,
              pendingAttempt,
            );
            if (!isCurrentAttempt(attempt, generation, token)) return;
            setPendingStorageUnavailable(!pendingPersisted);
            setStoredAttempt(pendingAttempt);
            setPendingOperation({
              generation,
              occurrenceCount: currentSessionCount,
              operationId: response.operationId,
              retryAfterSeconds: response.retryAfterSeconds ?? null,
              routeKey: attempt.routeKey,
              startAt: startTime,
              timezone: data.timezone,
            });
            setAttemptPhase('pending');
            return;
          }
          const errorMessage = ownedBookingErrorMessage(response.operationId);
          const pendingAttempt: StoredBookingAttempt = {
            ...submittedAttempt,
            errorMessage,
            pendingAppointmentIds: response.appointments.map(
              (appointment) => appointment.id,
            ),
            pendingOperationId: response.operationId,
            status: 'error',
          };
          if (!transitionBookingAttempt(submittedAttempt, pendingAttempt))
            return;
          if (!isCurrentAttempt(attempt, generation, token)) return;
          setStoredAttempt(pendingAttempt);
          setBookingError(errorMessage);
          setAttemptPhase('failed');
          return;
        }
        const pendingAttempt: StoredBookingAttempt = {
          ...submittedAttempt,
          confirmationCandidate: candidate,
          pendingAppointmentIds: candidate.appointmentIds,
          pendingOperationId: response.operationId,
          retryAfterSeconds: response.retryAfterSeconds ?? null,
          status: 'pending',
        };
        // A committed HTTP 202 admission is authoritative even when the browser
        // cannot persist it. Keep the operation in memory so polling continues
        // and resubmission stays blocked; the pending screen shows its
        // reference and hides the navigation that would drop it.
        const pendingPersisted = transitionBookingAttempt(
          submittedAttempt,
          pendingAttempt,
        );
        if (!isCurrentAttempt(attempt, generation, token)) return;
        setPendingStorageUnavailable(!pendingPersisted);
        setStoredAttempt(pendingAttempt);
        setConfirmationCandidate(candidate);
        setPendingOperation({
          generation,
          occurrenceCount: currentSessionCount,
          operationId: response.operationId,
          retryAfterSeconds: response.retryAfterSeconds ?? null,
          routeKey: attempt.routeKey,
          startAt: startTime,
          timezone: data.timezone,
        });
        // MEN-4915: confirm from the admission response. The booking is already
        // durable here -- every rejecting invariant ran inside the admission
        // transaction, and an admitted book operation can only end recovered or
        // needs_reconciliation, never rejected. The operation keeps being polled
        // for reconciliation and stored-attempt bookkeeping; it just no longer
        // gates what the member sees.
        setConfirmedSeries({
          firstStart: candidate.firstStart,
          intervalWeeks: candidate.cadenceWeeks,
          occurrenceCount: candidate.occurrenceCount,
          seriesId: candidate.seriesId,
          status: 'confirmed',
        });
        setConfirmationTimezone(candidate.timezone);
        setAttemptPhase('pending');
        return;
      }
      if (response.status === 'rejected') {
        const message = appointmentRejectionMessage(
          'rejection' in response
            ? response.rejection
            : participantRejection(response),
          'booking',
        );
        const rejectedAttempt: StoredBookingAttempt = {
          ...submittedAttempt,
          errorMessage: message,
          pendingOperationId: response.operationId,
          rejectedCanonicalDigest: submittedAttempt.canonicalDigest,
          status: 'rejected',
        };
        if (!transitionBookingAttempt(submittedAttempt, rejectedAttempt))
          return;
        if (!isCurrentAttempt(attempt, generation, token)) return;
        await finishRejectedAttempt({
          attempt: rejectedAttempt,
          detailAppointmentIds: [],
          message,
          token,
        });
        return;
      }
      const appointment = response.appointments[0];
      const candidate = confirmationCandidateFromMutation({
        cadenceWeeks: 2,
        calendarEventTitle: submittedAttempt.calendarEventTitle,
        durationMinutes: authoritativeSchedulingContext?.durationMinutes,
        expectedCoachPersonId: authoritativeCoachPersonId,
        expectedFirstStart: startTime,
        expectedMemberPersonId: authoritativeMemberPersonId,
        expectedOccurrenceCount: currentSessionCount,
        expectedSeatType: authoritativeSeatType,
        memberEmail,
        result: response,
      });
      if (!appointment || !candidate) {
        const errorMessage = ownedBookingErrorMessage(response.operationId);
        const errorAttempt: StoredBookingAttempt = {
          ...submittedAttempt,
          errorMessage,
          pendingAppointmentIds: response.appointments.map(
            (responseAppointment) => responseAppointment.id,
          ),
          pendingOperationId: response.operationId,
          status: 'error',
        };
        if (!transitionBookingAttempt(submittedAttempt, errorAttempt)) return;
        if (!isCurrentAttempt(attempt, generation, token)) return;
        setStoredAttempt(errorAttempt);
        setBookingError(errorMessage);
        setAttemptPhase('failed');
        return;
      }
      const confirmedAttempt: StoredBookingAttempt = {
        ...submittedAttempt,
        confirmationCandidate: candidate,
        pendingAppointmentIds: candidate.appointmentIds,
        pendingOperationId: response.operationId,
        status: 'confirmed',
      };
      if (!transitionBookingAttempt(submittedAttempt, confirmedAttempt)) return;
      if (!isCurrentAttempt(attempt, generation, token)) return;
      invalidateConfirmedAttempt(confirmedAttempt, true, token);
      setConfirmationCandidate(candidate);
      setConfirmedSeries({
        firstStart: candidate.firstStart,
        intervalWeeks: candidate.cadenceWeeks,
        meetUrl: appointment.meeting.joinUrl ?? undefined,
        occurrenceCount: candidate.occurrenceCount,
        seriesId: candidate.seriesId,
        status: appointment.bookingStatus,
      });
      setConfirmationTimezone(candidate.timezone);
      setAttemptPhase('confirmed');
    } catch (bookingRequestError) {
      if (!isCurrentAttempt(attempt, generation, token)) {
        return;
      }
      const originalError = bookingErrorMessage(bookingRequestError);
      if (isTrustedCalendarEventTitleStale(bookingRequestError)) {
        setRequiresFreshSchedulingContext(true);
        clearBookingAttempt(routeKey, 'submitting');
        setStoredAttempt(null);
        releaseSubmissionClaim();
        setSelectedSlot(null);
        setBookingError(originalError);
        setAttemptPhase('failed');
        try {
          await refetchAuthoritativeSchedulingContext().unwrap();
          if (isCurrentRouteInstance(token)) {
            setSelectedSlot(null);
            setRequiresFreshSchedulingContext(false);
          }
        } catch {
          // The trusted no-write result still requires a fresh selection.
        }
        return;
      }
      if (
        !(submittedAttempt.transport === 'moments'
          ? shouldReconcileParticipantAppointmentWrite(bookingRequestError)
          : shouldReconcileAppointmentWrite(bookingRequestError))
      ) {
        clearBookingAttempt(routeKey, 'submitting');
        setStoredAttempt(null);
        releaseSubmissionClaim();
        setBookingError(originalError);
        setAttemptPhase('failed');
        return;
      }

      const reconcilingAttempt: StoredBookingAttempt = {
        ...submittedAttempt,
        errorMessage: originalError,
        status: 'reconciling',
      };
      saveBookingAttempt(reconcilingAttempt);
      setStoredAttempt(reconcilingAttempt);
      setAttemptPhase('reconciling');
      try {
        await refetchAuthoritativeSchedulingContext().unwrap();
        if (!isCurrentAttempt(attempt, generation, token)) {
          return;
        }
        const errorAttempt: StoredBookingAttempt = {
          ...reconcilingAttempt,
          status: 'error',
        };
        saveBookingAttempt(errorAttempt);
        setStoredAttempt(errorAttempt);
        setBookingError(originalError);
        setAttemptPhase('failed');
      } catch {
        if (isCurrentAttempt(attempt, generation, token)) {
          const errorAttempt: StoredBookingAttempt = {
            ...reconcilingAttempt,
            status: 'error',
          };
          saveBookingAttempt(errorAttempt);
          setStoredAttempt(errorAttempt);
          setBookingError(originalError);
          setAttemptPhase('failed');
        }
      }
    }
  };

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center gap-16 bg-sunny-100 px-4 py-8">
      <IconButton
        aria-label="Close"
        className="absolute right-4 top-4"
        onClick={() => navigate(returnPath)}
      >
        <Icon>close</Icon>
      </IconButton>
      <img alt="Mento" className="h-8" src={MENTO_LOGO_URL} />
      <Paper
        aria-label="Book bi-weekly coaching"
        className="flex min-h-[421px] w-full max-w-[1141px] flex-col overflow-hidden lg:h-[421px] lg:flex-row"
        elevation={3}
        role="region"
        style={{ border: `1px solid ${DIVIDER}`, borderRadius: 16 }}
      >
        <section
          aria-label="Coaching details"
          className="flex shrink-0 flex-col gap-4 border-b p-8 lg:h-full lg:min-h-0 lg:w-[379px] lg:overflow-y-auto lg:border-b-0 lg:border-r"
          style={{ borderColor: DIVIDER }}
        >
          <div className="flex items-center gap-3.5">
            <Avatar
              firstName={coachFirstName}
              lastName={coachLastName}
              size="s"
              src={data.coach.avatarUrl ?? ''}
            />
            <Typography className="text-base font-bold leading-normal">
              {data.coach.fullName}
            </Typography>
          </div>

          <div>
            <Typography
              className="text-2xl font-bold leading-tight"
              variant="h5"
            >
              {isAdminMode
                ? `Book ${seatTypeTitle}`
                : `Start coaching with ${coachFirstName}`}
            </Typography>
          </div>

          <Typography className="text-base leading-normal text-cement-400">
            This is the start of your work together. Pick a time, and{' '}
            {coachFirstName} becomes your coach.
          </Typography>

          {/* MEN-3990: the Figma's icon-led rail, drawn by the shared row so
              this flow and the admin one cannot drift apart again. The rows
              themselves stay member-specific — seat type and calendar event
              title have no admin equivalent. */}
          <div className="flex flex-col gap-4">
            {memberEmail && (
              <MentoCalBookingDetailRow icon="account_box">
                {memberEmail}
              </MentoCalBookingDetailRow>
            )}
            <MentoCalBookingDetailRow icon="videocam">
              Google Meet
            </MentoCalBookingDetailRow>
            <MentoCalBookingDetailRow icon="work">
              {seatTypeLabel(authoritativeSeatType)}
            </MentoCalBookingDetailRow>
            <MentoCalBookingDetailRow icon="schedule">
              {data.durationMinutes} Minutes
            </MentoCalBookingDetailRow>
            <MentoCalBookingDetailRow icon="calendar_month">
              {seriesLengthLabel(currentSessionCount)}
            </MentoCalBookingDetailRow>
            <MentoCalBookingDetailRow icon="public">
              {data.timezone}
            </MentoCalBookingDetailRow>
            {authoritativeSchedulingContext?.contractVersion ===
            'appointment-scheduling-context.v2' ? (
              <MentoCalBookingDetailRow icon="event">
                {authoritativeSchedulingContext.calendarEventTitle}
              </MentoCalBookingDetailRow>
            ) : null}
          </div>
        </section>

        <section
          aria-label="Choose a date"
          className="flex min-w-0 flex-1 flex-col border-b p-8 lg:h-full lg:min-h-0 lg:w-[407px] lg:flex-none lg:overflow-y-auto lg:border-b-0 lg:border-r"
          style={{ borderColor: DIVIDER }}
        >
          <MentoCalDatePicker
            canShowMore={canShowMore}
            // MEN-3990: the Figma calendar is the roomy variant — 44px cells,
            // 4px gaps, SUN/MON headers, and a sage-100 fill on selectable
            // days, which the compact form renders as plain white.
            days={calendarDays}
            isLoadingMore={isFetching}
            selectedDate={selectedDayDate}
            comfortable
            onSelectDate={onSelectDay}
            onShowMore={onShowMore}
          />
        </section>

        <section
          aria-label="Choose a time"
          className="flex w-full flex-col justify-between gap-4 p-8 lg:h-full lg:min-h-0 lg:w-[355px] lg:shrink-0 lg:overflow-y-auto"
        >
          <MentoCalTimeList
            className="flex-1"
            heading={selectedDateLabel}
            // The card is only height-capped from `lg` up, where `lg:h-full`
            // gives `flex-1` something finite to clip against. Below that the
            // card is content-sized, so `flex-1` bounds nothing and a long day
            // would push the booking CTA off the page — the very thing this
            // ticket exists to stop. Hence an explicit cap at narrow widths,
            // released at `lg` so the desktop flex behaviour is unchanged.
            scrollClassName="relative max-h-80 flex-1 lg:max-h-none"
          >
            {isAdminMode && selectedDay ? (
              <>
                <button
                  aria-expanded={isTimeMenuOpen}
                  aria-haspopup="menu"
                  className="flex min-h-10 w-full items-center justify-center gap-2 bg-cement-100 px-3 py-2 text-[15px] font-bold"
                  type="button"
                  onClick={() => setIsTimeMenuOpen((open) => !open)}
                >
                  {validatedSlot?.label ?? 'Select a time'}
                  <Icon>arrow_drop_down</Icon>
                </button>
                {isTimeMenuOpen ? (
                  <div
                    aria-label="Available times"
                    className="flex max-h-[240px] flex-col overflow-y-auto bg-cement-100"
                    role="menu"
                  >
                    {availableSlots.map((slot) => (
                      <button
                        key={slotStartTime(slot)}
                        className="min-h-8 w-full px-8 text-left text-[15px] font-bold hover:bg-cement-200"
                        role="menuitem"
                        type="button"
                        onClick={() => {
                          setSelectedSlot(slot);
                          setIsTimeMenuOpen(false);
                        }}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              availableSlots.map((slot) => {
                const startTime = slotStartTime(slot);
                const isActive =
                  validatedSlot !== null &&
                  slotStartTime(validatedSlot) === startTime;

                return (
                  <MentoCalTimeSlotButton
                    key={startTime}
                    label={slot.label}
                    selected={isActive}
                    onClick={() => setSelectedSlot(slot)}
                  />
                );
              })
            )}
            {selectedDay && availableSlots.length === 0 && (
              <Typography className="text-sm text-cement-400">
                No times available.
              </Typography>
            )}
          </MentoCalTimeList>

          <div>
            <SeriesCoachConflictNote
              className="mb-2"
              conflictingOccurrenceStarts={selectedCoachConflictStarts}
              scope="series"
              // The time list and its timezone caption use the context
              // timezone, so the conflict dates must too.
              timeZone={data.timezone}
            />
            {(hasAdminCoachConflict || bookingError) && (
              <div className="mb-2 flex items-center justify-between gap-2">
                <Typography className="text-sm text-red-600">
                  {hasAdminCoachConflict
                    ? ADMIN_COACH_CONFLICT_MESSAGE
                    : bookingError}
                </Typography>
                {bookingError && attemptPhase === 'failed' ? (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setBookingError('')}
                  >
                    Dismiss
                  </Button>
                ) : null}
              </div>
            )}
            <div className="flex">
              <Button
                className="w-full rounded-xl bg-sage-500 px-5 py-2 text-[15px] font-bold tracking-[0.0267em] text-white shadow-none hover:bg-sage-500"
                disabled={
                  !validatedSlot ||
                  Boolean(getError) ||
                  Boolean(momentsContextQuery.error) ||
                  bookingResult.isLoading ||
                  momentsBookingResult.isLoading ||
                  attemptPhase === 'reconciling' ||
                  attemptPhase === 'submitting' ||
                  requiresFreshSchedulingContext ||
                  rejectedSelectionBlocked ||
                  hasAdminCoachConflict
                }
                size="small"
                variant="contained"
                onClick={onSchedule}
              >
                {bookingButtonLabel}
              </Button>
            </div>
          </div>
        </section>
      </Paper>
    </main>
  );
};

const MemberBiweeklyBooking = () => {
  const onBehalf = useOnBehalfBookingTarget();
  const navigate = useNavigate();
  if (onBehalf) {
    return (
      <PlatformAdminBookingPage
        bookingType="biweekly"
        coachId={onBehalf.coachId}
        kind="coaching"
        memberId={onBehalf.memberId}
        onDone={() => navigate(onBehalf.returnPath)}
      />
    );
  }
  return (
    <MemberBoundBooking>
      <ParticipantBiweeklyBooking />
    </MemberBoundBooking>
  );
};

export default MemberBiweeklyBooking;
